import { createCommentPinView } from './comment-pins.js';
import { isHistoryTransaction } from 'prosemirror-history';
import { Plugin, PluginKey, type EditorState, type Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { Node as ProseMirrorNode } from 'prosemirror-model';
import type { WebEditorComment } from '../comments.js';

export interface CommentPluginState {
  comments: readonly WebEditorComment[];
  activeId: string | null;
  enabled: boolean;
  orphanIds: ReadonlySet<string>;
}
export const commentsPluginKey = new PluginKey<CommentPluginState>('web-editor-comments');
export class CommentAnchorHistory {
  private readonly snapshots: Array<{ doc: ProseMirrorNode; comments: readonly WebEditorComment[] }> = [];
  remember(doc: ProseMirrorNode, comments: readonly WebEditorComment[]) { this.snapshots.push({ doc, comments }); if (this.snapshots.length > 200) this.snapshots.shift(); }
  find(doc: ProseMirrorNode) { return [...this.snapshots].reverse().find(snapshot => snapshot.doc.eq(doc)); }
}
export interface CommentsPluginOptions {
  history?: CommentAnchorHistory;
  locale: string | (() => string);
  comments?: readonly WebEditorComment[];
  onSelect?: (comment: WebEditorComment) => void;
  onMove?: (comment: WebEditorComment, anchor: WebEditorComment['anchor']) => void;
  /** Host integrations may resolve migrated stable IDs without persisting DOM selectors. */
  nodeId?: (node: ProseMirrorNode) => string | readonly string[] | undefined;
}

const nodeIndex = (doc: ProseMirrorNode, identify: NonNullable<CommentsPluginOptions['nodeId']>) => {
  const index = new Map<string, { pos: number; node: ProseMirrorNode }>();
  doc.descendants((node, pos) => { const id = identify(node); for (const value of Array.isArray(id) ? id : typeof id === 'string' ? [id] : []) index.set(value, { pos, node }); });
  return index;
};

export const createCommentsPlugin = (options: CommentsPluginOptions): Plugin<CommentPluginState> => {
  const snapshots = options.history ?? new CommentAnchorHistory();
  const identify = options.nodeId ?? (node => typeof node.attrs.id === 'string' ? node.attrs.id : undefined);
  const orphans = (doc: ProseMirrorNode, comments: readonly WebEditorComment[]) => {
    const index = nodeIndex(doc, identify);
    return new Set(comments.filter(comment => !index.has(comment.anchor.elementId)).map(comment => comment.id));
  };
  return new Plugin<CommentPluginState>({
    key: commentsPluginKey,
    state: {
      init: (_, state) => ({ comments: options.comments ?? [], activeId: null, enabled: false, orphanIds: orphans(state.doc, options.comments ?? []) }),
      apply: (tr, previous, oldState, nextState) => {
        const update = tr.getMeta(commentsPluginKey) as Partial<Pick<CommentPluginState, 'comments' | 'activeId' | 'enabled'>> | undefined;
        if (tr.docChanged) {
          snapshots.remember(oldState.doc, previous.comments);
        }
        const restored = tr.docChanged && isHistoryTransaction(tr) ? snapshots.find(nextState.doc) : undefined;
        const before = nodeIndex(oldState.doc, identify);
        const after = nodeIndex(nextState.doc, identify);
        const incoming = update?.comments?.map(comment => {
          const local = previous.comments.find(candidate => candidate.id === comment.id && candidate.revision === comment.revision);
          // Realtime refreshes must not reset ranges already mapped through unsaved local edits.
          return local ? { ...comment, anchor: local.anchor } : comment;
        });
        const comments = incoming ?? previous.comments.map(comment => {
          const historical = restored?.comments.find(saved => saved.id === comment.id && saved.revision === comment.revision && saved.anchor.elementId === comment.anchor.elementId);
          if (historical) return { ...comment, anchor: { ...historical.anchor } };
          const range = comment.anchor.range;
          const oldNode = before.get(comment.anchor.elementId);
          const newNode = after.get(comment.anchor.elementId);
          if (!tr.docChanged || !range || !oldNode || !newNode || range.locale !== (typeof options.locale === 'function' ? options.locale() : options.locale)) return comment;
          const from = tr.mapping.mapResult(oldNode.pos + 1 + range.from, 1);
          const to = tr.mapping.mapResult(oldNode.pos + 1 + range.to, -1);
          // Moving a complete node may delete/reinsert its positions. Identity still wins.
          if (from.deleted && to.deleted && oldNode.node.eq(newNode.node)) return comment;
          const clamp = (pos: number) => Math.max(0, Math.min(newNode.node.content.size, pos - newNode.pos - 1));
          const nextFrom = clamp(from.pos);
          const nextTo = Math.max(nextFrom, clamp(to.pos));
          return { ...comment, anchor: { ...comment.anchor, range: { ...range, from: nextFrom, to: nextTo } } };
        });
        return { ...previous, ...update, comments, orphanIds: orphans(nextState.doc, comments) };
      },
    },
    view: view => createCommentPinView(view, {
      comments: candidate => { const state = commentsPluginKey.getState(candidate.state); return state?.enabled ? state.comments : []; },
      position: (candidate, id) => nodeIndex(candidate.state.doc, identify).get(id)?.pos,
      select: (candidate, comment) => { candidate.dispatch(updateComments(candidate.state, { activeId: comment.id })); options.onSelect?.(comment); },
      move: options.onMove,
    }),
    props: {
      decorations: state => {
        const plugin = commentsPluginKey.getState(state);
        if (!plugin?.enabled) return DecorationSet.empty;
        const index = nodeIndex(state.doc, identify);
        const decorations: Decoration[] = [];
        for (const comment of plugin.comments) {
          const target = index.get(comment.anchor.elementId);
          if (!target || comment.status !== 'open') continue;
          const range = comment.anchor.range;
          if (range && range.locale === (typeof options.locale === 'function' ? options.locale() : options.locale) && range.from < range.to) {
            const from = target.pos + 1 + Math.min(range.from, target.node.content.size);
            const to = target.pos + 1 + Math.min(range.to, target.node.content.size);
            if (from < to) decorations.push(Decoration.inline(from, to, { class: 'we-comment-range', 'data-comment-id': comment.id }));
          }

        }
        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
};

export const updateComments = (state: EditorState, update: Partial<Pick<CommentPluginState, 'comments' | 'activeId' | 'enabled'>>): Transaction =>
  state.tr.setMeta(commentsPluginKey, update).setMeta('addToHistory', false);
